import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SubscriptionPlan } from '../../../types';
import { SubscriptionPlanService } from '../../../services';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageSubscriptionPlans } from '../../../utils/permissions';
import { formatPrice } from '../../../utils';
import { CheckoutLinkTrackingService } from '../../../services/CheckoutLinkTrackingService';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import Button from '../../../components/common/Button/Button';
import DataTable from '../../../components/common/DataTable/DataTable';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import Loading from '../../../components/common/Loading/Loading';
import FilterBar from '../../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../../components/common/FilterGroup/FilterGroup';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import SearchFilterBar from '../../../components/common/SearchFilterBar/SearchFilterBar';
import SingleSelectFilter from '../../../components/common/SingleSelectFilter/SingleSelectFilter';
import ClearFiltersButton from '../../../components/common/ClearFiltersButton/ClearFiltersButton';
import RecordCount from '../../../components/common/RecordCount/RecordCount';
import PageSizeSelect from '../../../components/common/PageSizeSelect/PageSizeSelect';
import Pagination from '../../../components/common/Pagination/Pagination';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { useMarketingProjects } from '../../../hooks/useMarketingProjects';
import type { PaginationInfo } from '../../../types';
import SubscriptionPlanModal from '../modals/SubscriptionPlanModal';
import PlanNameWithProject from '../../../components/common/PlanNameWithProject/PlanNameWithProject';
import Tooltip from '../../../components/common/Tooltip';
import styles from './SubscriptionPlansTab.module.css';

const GRID_TEMPLATE = '2fr 1fr 1fr 1fr 90px 80px 80px 72px';
const HEADERS = ['Nome', 'Preço', 'Ciclo', 'Fidelidade', 'À Vista', 'Ativo', 'App', ''];

const INITIAL_PAGINATION: PaginationInfo = {
  page: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const DEFAULT_ACTIVE_FILTER = 'true';
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;

const ACTIVE_FILTER_OPTIONS = [
  { value: 'true', label: 'Ativo' },
  { value: 'false', label: 'Inativo' },
  { value: 'all', label: 'Todos' },
];

const resolveActiveFilterValues = (value: string): boolean[] => {
  if (value === 'false') {
    return [false];
  }

  if (value === 'all') {
    return [true, false];
  }

  return [true];
};

const BILLING_CYCLE_LABELS: Record<string, string> = {
  mensal: 'Mensal',
  monthly: 'Mensal',
  bimestral: 'Bimestral',
  bimonthly: 'Bimestral',
  semanal: 'Semanal',
  weekly: 'Semanal',
  quinzenal: 'Quinzenal',
  biweekly: 'Quinzenal',
  trimestral: 'Trimestral',
  quarterly: 'Trimestral',
  semestral: 'Semestral',
  semiannually: 'Semestral',
  anual: 'Anual',
  annual: 'Anual',
  yearly: 'Anual',
};

const BILLING_CYCLE_FILTER_OPTIONS = [
  { value: 'monthly', label: 'Mensal' },
  { value: 'bimonthly', label: 'Bimestral' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'yearly', label: 'Anual' },
];
const SUBSCRIPTION_FILTER_SCHEMA = {
  page: queryFilter.integer(1, { min: 1, param: 'plansPage' }),
  pageSize: queryFilter.integer(10, { allowed: PAGE_SIZE_OPTIONS, param: 'plansPageSize' }),
  searchName: queryFilter.string('', { param: 'plansSearch' }),
  isActive: queryFilter.enum(DEFAULT_ACTIVE_FILTER, ['true', 'false', 'all'], { param: 'plansIsActive' }),
  billingCycles: queryFilter.array<string>([], { param: 'plansBillingCycles' }),
  loyaltyMonths: queryFilter.array<string>([], { param: 'plansLoyaltyMonths' }),
  projectCodes: queryFilter.array<string>([], { param: 'plansProjectCodes' }),
};

const formatBillingCycle = (value: string): string =>
  BILLING_CYCLE_LABELS[value.trim().toLowerCase()] ?? value;

const formatLoyaltyMonthsLabel = (months: number): string => (
  months === 0 ? 'Sem fidelidade' : `${months} ${months === 1 ? 'mês' : 'meses'}`
);

const isSinglePaymentPlan = (plan: SubscriptionPlan): boolean => (
  Boolean(plan.is_single_payment) || plan.billing_cycle.trim().toLowerCase() !== 'monthly'
);

const SubscriptionPlansTab: React.FC = () => {
  const { roles } = useAuth();
  const canManage = canManageSubscriptionPlans(roles);
  const { filters, setFilter, setFilters } = useQueryFilters(
    SUBSCRIPTION_FILTER_SCHEMA,
    { pageKey: 'page' },
  );
  const {
    page,
    pageSize,
    searchName: filterSearchName,
    isActive: filterIsActive,
    billingCycles: filterBillingCycles,
    loyaltyMonths: filterLoyaltyMonths,
    projectCodes: filterProjectCodes,
  } = filters;
  const projects = useMarketingProjects();
  const projectFilterOptions = useMemo(
    () => projects.map((project) => ({
      value: project.code,
      label: project.code,
      color: project.colors.background,
    })),
    [projects],
  );

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [saving, setSaving] = useState(false);
  const [copyingPlanId, setCopyingPlanId] = useState<number | null>(null);
  const [copiedPlanId, setCopiedPlanId] = useState<number | null>(null);
  const [availableLoyaltyMonths, setAvailableLoyaltyMonths] = useState<number[]>([]);

  const loyaltyMonthOptions = useMemo(
    () => availableLoyaltyMonths.map((months) => ({
      value: String(months),
      label: formatLoyaltyMonthsLabel(months),
    })),
    [availableLoyaltyMonths],
  );

  const hasActiveFilters = useMemo(() => (
    Boolean(filterSearchName.trim())
    || filterIsActive !== DEFAULT_ACTIVE_FILTER
    || filterBillingCycles.length > 0
    || filterLoyaltyMonths.length > 0
    || filterProjectCodes.length > 0
  ), [
    filterBillingCycles,
    filterIsActive,
    filterLoyaltyMonths,
    filterProjectCodes,
    filterSearchName,
  ]);

  const loadPlans = useCallback(async (
    targetPage: number,
    targetPageSize: number,
    searchName: string,
    activeFilter: string,
    billingCycles: string[],
    loyaltyMonths: string[],
    projectCodes: string[],
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await SubscriptionPlanService.listPaginated(
        targetPage,
        targetPageSize,
        {
          searchName,
          filterIsActive: resolveActiveFilterValues(activeFilter),
          billingCycles,
          loyaltyMonths: loyaltyMonths
            .map((value) => Number.parseInt(value, 10))
            .filter((value) => Number.isInteger(value) && value >= 0),
          projectCodes,
        },
      );

      setPlans(response.data);
      setPagination(response.pagination);
      setAvailableLoyaltyMonths(response.meta?.availableLoyaltyMonths ?? []);
    } catch {
      setError('Erro ao carregar planos de assinatura.');
      setPlans([]);
      setPagination((prev) => ({
        ...prev,
        page: targetPage,
        pageSize: targetPageSize,
        totalRecords: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans(
      page,
      pageSize,
      filterSearchName,
      filterIsActive,
      filterBillingCycles,
      filterLoyaltyMonths,
      filterProjectCodes,
    );
  }, [
    filterBillingCycles,
    filterIsActive,
    filterLoyaltyMonths,
    filterProjectCodes,
    filterSearchName,
    loadPlans,
    page,
    pageSize,
  ]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      searchName: '',
      isActive: DEFAULT_ACTIVE_FILTER,
      billingCycles: [],
      loyaltyMonths: [],
      projectCodes: [],
    });
  }, [setFilters]);

  const handleOpenCreate = () => {
    setEditingPlan(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setModalOpen(true);
  };

  const handleCopyCheckoutLink = async (plan: SubscriptionPlan) => {
    if (copyingPlanId !== null) {
      return;
    }

    setCopyingPlanId(plan.id);

    try {
      const trackedLink = await CheckoutLinkTrackingService.generate(plan.id);
      await navigator.clipboard.writeText(trackedLink.url);
      setCopiedPlanId(plan.id);
      window.setTimeout(() => {
        setCopiedPlanId((current) => (current === plan.id ? null : current));
      }, 2500);
    } catch (copyError) {
      alert(copyError instanceof Error ? copyError.message : 'Não foi possível copiar o link de checkout.');
    } finally {
      setCopyingPlanId(null);
    }
  };

  const getCopyLinkIcon = (planId: number): string => {
    if (copyingPlanId === planId) {
      return 'progress_activity';
    }

    if (copiedPlanId === planId) {
      return 'done';
    }

    return 'content_copy';
  };

  const handleCancel = () => {
    setModalOpen(false);
    setEditingPlan(null);
  };

  const handleDelete = async (plan: SubscriptionPlan) => {
    setSaving(true);
    try {
      await SubscriptionPlanService.delete(plan.id);
      setModalOpen(false);
      setEditingPlan(null);
      await loadPlans(
        page,
        pageSize,
        filterSearchName,
        filterIsActive,
        filterBillingCycles,
        filterLoyaltyMonths,
        filterProjectCodes,
      );
    } catch {
      throw new Error('Erro ao excluir plano.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (payload: Partial<SubscriptionPlan>) => {
    setSaving(true);
    try {
      if (editingPlan) {
        await SubscriptionPlanService.update(editingPlan.id, payload);
      } else {
        await SubscriptionPlanService.create(payload);
      }
      setModalOpen(false);
      setEditingPlan(null);
      await loadPlans(
        page,
        pageSize,
        filterSearchName,
        filterIsActive,
        filterBillingCycles,
        filterLoyaltyMonths,
        filterProjectCodes,
      );
    } catch {
      throw new Error('Erro ao salvar plano.');
    } finally {
      setSaving(false);
    }
  };

  if (error && plans.length === 0 && !loading) {
    return <p className={styles.error}>{error}</p>;
  }

  return (
    <>
      <div className={styles.sectionHeader}>
        <SectionTitle as="h2" text="Planos de assinatura" />
        {canManage && (
          <Button variant="primary" size="small" onClick={handleOpenCreate}>
            Novo plano
          </Button>
        )}
      </div>

      <FilterBar gap="sm" pushFirstChild>
        <RecordCount
          count={`${pagination.totalRecords} ${pagination.totalRecords === 1 ? 'plano' : 'planos'}`}
          className={styles['record-count']}
        />
        <FilterGroup>
          <SearchFilterBar
            searchValue={filterSearchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Pesquisar plano"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          {projectFilterOptions.length > 0 && (
            <MultiSelectFilter
              values={filterProjectCodes}
              onChange={(values) => setFilter('projectCodes', values)}
              options={projectFilterOptions}
              placeholder="Projeto"
              searchPlaceholder="Pesquisar"
              noResultsText="Nenhum projeto encontrado"
              clearButtonLabel="Limpar filtro"
              hideChips
              showSelectionCount={false}
            />
          )}
          <SingleSelectFilter
            value={filterIsActive}
            onChange={(value) => setFilter('isActive', value as 'true' | 'false' | 'all')}
            options={ACTIVE_FILTER_OPTIONS}
            placeholder="Status"
          />
          <MultiSelectFilter
            values={filterBillingCycles}
            onChange={(values) => setFilter('billingCycles', values)}
            options={BILLING_CYCLE_FILTER_OPTIONS}
            placeholder="Ciclo"
            hideChips
          />
          <MultiSelectFilter
            values={filterLoyaltyMonths}
            onChange={(values) => setFilter('loyaltyMonths', values)}
            options={loyaltyMonthOptions}
            placeholder="Fidelidade"
            hideChips
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={handleClearFilters} />
        )}
      </FilterBar>

      {loading ? (
        <Loading />
      ) : plans.length === 0 ? (
        <EmptyState icon="subscriptions" message="Nenhum plano encontrado." />
      ) : (
        <>
          <DataTable headers={HEADERS} gridTemplate={GRID_TEMPLATE} borderedRows boldHeaders>
            {plans.map((plan) => (
              <DataTable.Row key={plan.id}>
                <div className={styles['name-cell']}>
                  <PlanNameWithProject
                    name={plan.name}
                    project={plan.project}
                    nameClassName={styles['col-name']}
                  />
                </div>
                <span>{formatPrice(plan.price_cents)}</span>
                <span>{formatBillingCycle(plan.billing_cycle)}</span>
                <span>{formatLoyaltyMonthsLabel(plan.loyalty_months)}</span>
                <span>
                  <span className={isSinglePaymentPlan(plan) ? styles['badge-single'] : styles['badge-recurring']}>
                    {isSinglePaymentPlan(plan) ? 'Sim' : 'Não'}
                  </span>
                </span>
                <span>
                  <span className={plan.is_active ? styles['badge-active'] : styles['badge-inactive']}>
                    {plan.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </span>
                <span>
                  <span className={plan.is_visible_in_app !== false ? styles['badge-active'] : styles['badge-inactive']}>
                    {plan.is_visible_in_app !== false ? 'Sim' : 'Não'}
                  </span>
                </span>
                <span className={styles.actions}>
                  {canManage && (
                    <>
                      <Tooltip content="Copiar link de checkout">
                        <button
                          className={styles['action-btn']}
                          onClick={() => {
                            void handleCopyCheckoutLink(plan);
                          }}
                          aria-label="Copiar link de checkout"
                          type="button"
                          disabled={copyingPlanId !== null}
                        >
                          <span
                            className={`material-symbols-outlined ${copyingPlanId === plan.id ? styles.spinner : ''}`.trim()}
                          >
                            {getCopyLinkIcon(plan.id)}
                          </span>
                        </button>
                      </Tooltip>
                      <Tooltip content="Editar plano">
                        <button
                          className={styles['action-btn']}
                          onClick={() => handleOpenEdit(plan)}
                          aria-label="Editar plano"
                          type="button"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                      </Tooltip>
                    </>
                  )}
                </span>
              </DataTable.Row>
            ))}
          </DataTable>

          <div className={styles['bottom-row']}>
            <PageSizeSelect
              value={pageSize}
              options={PAGE_SIZE_OPTIONS}
              onChange={(event) => setFilter('pageSize', Number.parseInt(event.target.value, 10))}
            />
            <div className={styles['pagination-center']}>
              <Pagination
                pagination={pagination}
                loading={loading}
                onPageChange={(targetPage) => setFilter('page', targetPage, { resetPage: false })}
              />
            </div>
          </div>
        </>
      )}

      <SubscriptionPlanModal
        open={modalOpen}
        plan={editingPlan}
        loading={saving}
        canManage={canManage}
        onConfirm={handleSave}
        onDelete={handleDelete}
        onCancel={handleCancel}
      />
    </>
  );
};

export default SubscriptionPlansTab;
